import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Download,
  TrendingUp,
  DollarSign,
  BarChart3,
  PieChart,
  RefreshCw,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { LoadingCard } from '../../components/ui/LoadingCard';
import { ErrorCard } from '../../components/ui/ErrorCard';
import { api } from '../../lib/api';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from 'recharts';

interface ReportData {
  transactionVolume: {
    labels: string[];
    data: number[];
    total: number;
  };
  revenue: {
    labels: string[];
    data: number[];
    total: number;
  };
  byType: {
    type: string;
    count: number;
    amount: number;
  }[];
}

const COLORS = ['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#6366f1'];

const periodOptions = [
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
  { value: 'custom', label: 'Período personalizado' },
];

const exportFormatOptions = [
  { value: 'pdf', label: 'PDF' },
  { value: 'excel', label: 'Excel' },
  { value: 'csv', label: 'CSV' },
];

export function Reports() {
  const [period, setPeriod] = useState('30d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exportFormat, setExportFormat] = useState('pdf');

  // Fetch report data
  const { data: reportData, isLoading, error, refetch } = useQuery<ReportData>({
    queryKey: ['reports', period, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (period !== 'custom') {
        params.append('period', period);
      } else {
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
      }
      const response = await api.get(`/reports/summary?${params.toString()}`);
      return response.data;
    },
    retry: (failureCount, error: any) => {
      // Don't retry on 404 - endpoint may not exist yet
      if (error?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: async (format: string) => {
      const params = new URLSearchParams();
      params.append('format', format);
      if (period !== 'custom') {
        params.append('period', period);
      } else {
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
      }
      const response = await api.get(`/reports/export?${params.toString()}`, {
        responseType: 'blob',
      });
      
      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  });

  const handleExport = () => {
    exportMutation.mutate(exportFormat);
  };

  // Transform data for charts
  const volumeChartData = reportData?.transactionVolume?.labels?.map((label, index) => ({
    name: label,
    volume: reportData.transactionVolume.data[index] || 0,
  })) || [];

  const revenueChartData = reportData?.revenue?.labels?.map((label, index) => ({
    name: label,
    receita: (reportData.revenue.data[index] || 0) / 100,
  })) || [];

  const pieChartData = reportData?.byType?.map((item) => ({
    name: item.type,
    value: item.count,
  })) || [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Relatórios</h1>
            <p className="text-surface-400">Análise de transações e receita</p>
          </div>
        </div>
        <LoadingCard rows={5} showHeader />
      </div>
    );
  }

  if (error) {
    const is404 = (error as any)?.response?.status === 404;
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Relatórios</h1>
            <p className="text-surface-400">Análise de transações e receita</p>
          </div>
        </div>
        {is404 ? (
          <Card>
            <div className="text-center py-12">
              <BarChart3 className="w-12 h-12 text-surface-600 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-surface-300 mb-1">Nenhum relatório disponível</h3>
              <p className="text-sm text-surface-500">Os relatórios financeiros serão exibidos aqui quando houver dados de transações.</p>
            </div>
          </Card>
        ) : (
          <ErrorCard message="Erro ao carregar relatórios" onRetry={() => refetch()} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Relatórios</h1>
          <p className="text-surface-400">Análise de transações e receita</p>
        </div>
        <Button variant="ghost" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>
      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-surface-300 mb-1">
              Período
            </label>
            <Select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              options={periodOptions}
            />
          </div>

          {period === 'custom' && (
            <>
              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Data Inicial
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Data Final
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="flex items-center gap-2">
            <Select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              options={exportFormatOptions}
              className="w-24"
            />
            <Button
              onClick={handleExport}
              disabled={exportMutation.isPending}
            >
              <Download className="w-4 h-4 mr-2" />
              {exportMutation.isPending ? 'Exportando...' : 'Exportar'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-surface-400">Volume de Transações</p>
              <p className="text-2xl font-bold text-white mt-1">
                {reportData?.transactionVolume?.total?.toLocaleString() || 0}
              </p>
            </div>
            <div className="p-3 bg-primary-500/20 rounded-lg">
              <BarChart3 className="w-6 h-6 text-primary-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-surface-400">Receita Total</p>
              <p className="text-2xl font-bold text-white mt-1">
                R$ {((reportData?.revenue?.total || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="p-3 bg-green-500/20 rounded-lg">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-surface-400">Tipos de Transação</p>
              <p className="text-2xl font-bold text-white mt-1">
                {reportData?.byType?.length || 0}
              </p>
            </div>
            <div className="p-3 bg-purple-500/20 rounded-lg">
              <PieChart className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Transaction Volume Chart */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary-400" />
            Volume de Transações
          </h3>
          <div className="h-[300px]">
            {volumeChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={volumeChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="volume" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-surface-400">
                Sem dados para exibir
              </div>
            )}
          </div>
        </Card>

        {/* Revenue Chart */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            Receita
          </h3>
          <div className="h-[300px]">
            {revenueChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={revenueChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [`R$ ${(value as number).toFixed(2)}`, 'Receita']}
                  />
                  <Line
                    type="monotone"
                    dataKey="receita"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ fill: '#10b981' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-surface-400">
                Sem dados para exibir
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Transaction Types Pie Chart */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <PieChart className="w-5 h-5 text-purple-400" />
          Distribuição por Tipo de Transação
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-[300px]">
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  >
                    {pieChartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-surface-400">
                Sem dados para exibir
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex flex-col justify-center">
            <h4 className="text-sm font-medium text-surface-300 mb-4">Detalhamento</h4>
            <div className="space-y-3">
              {reportData?.byType?.map((item, index) => (
                <div key={item.type} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-sm text-surface-300">{item.type}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-white">{item.count}</span>
                    <span className="text-xs text-surface-400 ml-2">
                      (R$ {(item.amount / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })})
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default Reports;
